import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../../Assets/Styles/pages/homeRedesign.scss";
import "../../Assets/Styles/pages/addTemplateRedesign.scss";

import AppSidebar from "../../Components/Layout/AppSidebar";
import AppTopbar from "../../Components/Layout/AppTopbar";
import UploadSingleImageButton from "../../Components/Buttons/UploadSingleImageButton";
import TemplateListSlider from "../../Components/TemplateListSlider/templateListSlider";
import SignIn from "../../Components/SignIn/signIn";
import { GetSettings } from "../../actions/Settings/Setting";
import { GetAllTemplates } from "../../actions/Template/Template";
import { setLoader } from "../../Redux/MainImage/imageSlice";
import SettingsModal from "../../Components/SettingsModal/settingsModal";
import { Backdrop, CircularProgress } from "@mui/material";
import { auth } from "../../Config/firebase-config";
import textIcon from "../../Assets/Images/text_nav.svg";
import signIcon from "../../Assets/Images/sign_nav.svg";
import logo from "../../Assets/Images/landing/eww-logo.png";
import type { RootState } from "../../Redux/store";
import usePageMetadata from "../../Utils/usePageMetadata";

const AddTemplate: React.FC = () => {
  usePageMetadata("My Templates - eZy WaterMark");
  const dispatch = useDispatch();
  const [openSignIn, setOpenSignIn] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);
  // Purely decorative preview panel -- lets the user click Text/Signature/Logo to see how
  // each watermark style looks, no data/logic wired to it.
  const [previewStyle, setPreviewStyle] = useState<"text" | "signature" | "logo">("signature");

  const file = useSelector((state: RootState) => state.mainImage);
  const userSliceState = useSelector((state: RootState) => state.userSlice);
  const isLoggedIn = Boolean(Object.keys(userSliceState.user_Auth ?? {}).length);
  const loader = file.loader;

  // Same displayName-resolution pattern as ProfilePage.tsx.
  const firebaseUser = auth.currentUser;
  const displayName =
    firebaseUser?.displayName ||
    (userSliceState.user_Auth?.displayName as string) ||
    "there";

  useEffect(() => {
    // GetAllTemplates() only ever turns the loader OFF on success -- nothing was turning it
    // ON before this fetch, so on a fresh visit to this page the Backdrop spinner below never
    // appeared while "My Templates" was loading; the list just silently populated whenever
    // the request resolved.
    dispatch(setLoader(true));
    void Promise.all([dispatch(GetAllTemplates()), dispatch(GetSettings())]);
  }, [dispatch]);

  return (
    <>
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <div className="home-shell add-template-shell">
        <AppSidebar
          active="templates"
          isLoggedIn={isLoggedIn}
          setOpenSignIn={setOpenSignIn}
          showPremiumUpsell
        />

        <div className="d-flex flex-column flex-fill" style={{ minWidth: 0 }}>
          <AppTopbar
            isLoggedIn={isLoggedIn}
            setOpenSignIn={setOpenSignIn}
            setOpenSettings={setOpenSettings}
          />

        <div className="add-template-main">
          <div className="add-template-hero">
            <div className="add-template-hero__copy">
              <div className="add-template-hero__welcome">
                Welcome back, {displayName}! 👋
              </div>
              <h1 className="add-template-hero__title">
                Create stunning{" "}
                <span className="add-template-hero__accent">watermarks</span> for
                your photos
              </h1>
              <p className="add-template-hero__subtitle">
                Create, edit and organize your custom watermark templates. Select
                any template to start using it on your photos.
              </p>

              {/* Same UploadSingleImageButton/cause="newTemplate" as before -- only the
                  surrounding CSS changed (a-template-hero__create restyles it into a
                  pill button instead of the old dashed dropzone look), no click-handler
                  logic touched. */}
              <div className="add-template-hero__create">
                <UploadSingleImageButton cause="newTemplate" />
              </div>

              <div className="add-template-hero__trust">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 3 4 6.5V11c0 4.6 3.2 8.9 8 10 4.8-1.1 8-5.4 8-10V6.5L12 3Z"
                    stroke="#22A06B"
                    strokeWidth="1.6"
                    strokeLinejoin="round"
                  />
                  <path
                    d="m9 12 2 2 4-4"
                    stroke="#22A06B"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Your templates are safe and secure
              </div>
            </div>

            <div className="add-template-hero__preview">
              <div className="add-template-hero__preview-card">
                <div className="add-template-hero__preview-selection">
                  {previewStyle === "text" && (
                    <span className="add-template-hero__preview-text">John Doe</span>
                  )}
                  {previewStyle === "signature" && (
                    <span className="add-template-hero__preview-signature">
                      John Doe
                    </span>
                  )}
                  {previewStyle === "logo" && (
                    <img
                      src={logo}
                      alt=""
                      className="add-template-hero__preview-logo"
                    />
                  )}
                  <span className="add-template-hero__preview-handle add-template-hero__preview-handle--tl" />
                  <span className="add-template-hero__preview-handle add-template-hero__preview-handle--tr" />
                  <span className="add-template-hero__preview-handle add-template-hero__preview-handle--bl" />
                  <span className="add-template-hero__preview-handle add-template-hero__preview-handle--br" />
                  <span className="add-template-hero__preview-resize" aria-hidden="true">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                      <path d="M6 18 18 6M11 18h7V11" stroke="#ffffff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="add-template-hero__preview-rail">
                <button
                  type="button"
                  className={`add-template-hero__preview-rail-item ${previewStyle === "text" ? "active" : ""}`}
                  onClick={() => setPreviewStyle("text")}
                >
                  <img src={textIcon} alt="" />
                  Text
                </button>
                <button
                  type="button"
                  className={`add-template-hero__preview-rail-item ${previewStyle === "signature" ? "active" : ""}`}
                  onClick={() => setPreviewStyle("signature")}
                >
                  <img src={signIcon} alt="" />
                  Signature
                </button>
                <button
                  type="button"
                  className={`add-template-hero__preview-rail-item ${previewStyle === "logo" ? "active" : ""}`}
                  onClick={() => setPreviewStyle("logo")}
                >
                  <img src={logo} alt="" className="add-template-hero__preview-rail-logo" />
                  Logo
                </button>
              </div>
            </div>
          </div>

          <TemplateListSlider />

          <div className="add-template-tip">
            <span aria-hidden="true">💡</span> Tip: You can edit or delete your
            templates anytime.
          </div>
        </div>
        </div>
      </div>

      <SettingsModal
        open={openSettings}
        setOpen={setOpenSettings}
        handleDone={() => setOpenSettings(false)}
        parent={false}
      />
      <SignIn open={openSignIn} setOpen={setOpenSignIn} />
    </>
  );
};

export default AddTemplate;
